import { z } from "zod";
import {
  SystemEntity,
  TASK_COMPLETION_MODES,
  TaskExpectedSubtaskSchema,
  TaskOutcomeFieldsSchema,
  TaskTypeActionsSchema,
  TaskTypeMetricsSchema,
  TaskTypeViewSchema,
} from "@erp/shared-types";
import {
  BaseImportFieldNodeSchema,
  FieldUIConfigSchema,
  SchemaModeSchema,
} from "./core/schema-definitions.contract";
import { parseVersionedBundle } from "./shared/parseVersionedBundle";

/**
 * Portable JSON bundle of task types and the Core Enums / Schema Definitions
 * they depend on. Ids never cross environments: dependencies are named by a
 * bundle-local `ref` and re-resolved (reused or created) on import.
 * Org-specific references (assignees, escalation targets, viewer groups,
 * playbook SOPs) are left out; the default namespace travels by key.
 */

export const TASK_TYPE_BUNDLE_FORMAT = "erp.task-type-bundle";
export const TASK_TYPE_BUNDLE_FORMAT_VERSION = 1;
export const MAX_TASK_TYPE_BUNDLE_TASK_TYPES = 200;

const BundleRefSchema = z.string().trim().min(1).max(300);

// Mirrors TaskTypeKeySchema in taskTypes.contract (kept local to avoid a cycle).
const BundleTaskTypeKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(100)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);

export const TaskTypeBundleEnumSchema = z.object({
  ref: BundleRefSchema,
  name: z.string().min(1).max(255),
  description: z.string().max(10000),
  areOptionsLocked: z.boolean(),
  // Exported from a global (organizationId null) enum. Import reuses a global
  // enum of the same name; it never creates a global one.
  global: z.boolean(),
  options: z
    .array(
      z.object({
        name: z.string().min(1).max(255),
        description: z.string().nullable(),
        sortOrder: z.number().int(),
      }),
    )
    .max(1000),
});

const BundleSchemaFieldBaseSchema = BaseImportFieldNodeSchema.extend({
  isMultiple: z.boolean().optional(),
  // Ref into `enums` for an enum field backed by a Core Enum.
  coreEnumRef: BundleRefSchema.nullable().optional(),
  uiConfig: FieldUIConfigSchema,
});

type BundleSchemaFieldBase = z.infer<typeof BundleSchemaFieldBaseSchema>;

export type TaskTypeBundleSchemaField = BundleSchemaFieldBase & {
  children?: TaskTypeBundleSchemaField[];
};

export const TaskTypeBundleSchemaFieldSchema: z.ZodType<TaskTypeBundleSchemaField> =
  BundleSchemaFieldBaseSchema.extend({
    children: z.lazy(() => z.array(TaskTypeBundleSchemaFieldSchema)).optional(),
  });

/**
 * A bundle travels both ways (export response, import request), so its schema
 * type stays a plain enum. `extension` is the retired name of an extraction
 * variant: bundles exported before the rename still carry it, and import reads
 * it as `extraction`.
 */
const BundleSchemaTypeSchema = z.enum(["extraction", "extension", "sql"]);

export const TaskTypeBundleSchemaDefinitionSchema = z.object({
  ref: BundleRefSchema,
  name: z.string().min(1).max(255),
  slug: z.string().min(1).max(255),
  description: z.string().nullable(),
  targetModel: z.string().min(1),
  schemaType: BundleSchemaTypeSchema,
  mode: SchemaModeSchema.nullable(),
  fields: z.array(TaskTypeBundleSchemaFieldSchema).max(500),
});

export const TaskTypeBundleTaskTypeSchema = z.object({
  key: BundleTaskTypeKeySchema,
  name: z.string().min(1).max(255),
  description: z.string().max(10000).nullable(),
  anchorEntityType: z.enum(SystemEntity).nullable(),
  completionMode: z.enum(TASK_COMPLETION_MODES),
  expectedSubtasks: z.array(TaskExpectedSubtaskSchema).max(50).nullable(),
  outcomeEnumRef: BundleRefSchema.nullable(),
  outcomeFields: TaskOutcomeFieldsSchema.nullable(),
  actions: TaskTypeActionsSchema.nullable(),
  inputSchemaRef: BundleRefSchema.nullable(),
  view: TaskTypeViewSchema.nullable(),
  metrics: TaskTypeMetricsSchema.nullable(),
  // Default namespace, matched by key on import (cleared when absent there).
  taskNamespaceKey: z.string().min(1).nullable(),
});

function collectFieldEnumRefs(
  fields: readonly TaskTypeBundleSchemaField[],
  into: string[],
): string[] {
  for (const field of fields) {
    if (field.coreEnumRef) into.push(field.coreEnumRef);
    if (field.children) collectFieldEnumRefs(field.children, into);
  }
  return into;
}

function duplicates(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const value of values) {
    if (seen.has(value)) dupes.add(value);
    seen.add(value);
  }
  return [...dupes];
}

export const TaskTypeBundleSchema = z
  .object({
    format: z.literal(TASK_TYPE_BUNDLE_FORMAT),
    formatVersion: z.literal(TASK_TYPE_BUNDLE_FORMAT_VERSION),
    exportedAt: z.string(),
    taskTypes: z
      .array(TaskTypeBundleTaskTypeSchema)
      .min(1)
      .max(MAX_TASK_TYPE_BUNDLE_TASK_TYPES),
    enums: z.array(TaskTypeBundleEnumSchema).max(500),
    schemas: z.array(TaskTypeBundleSchemaDefinitionSchema).max(200),
  })
  .superRefine((bundle, ctx) => {
    const enumRefs = new Set(bundle.enums.map((e) => e.ref));
    const schemaRefs = new Set(bundle.schemas.map((s) => s.ref));
    for (const [path, values] of [
      ["enums", bundle.enums.map((e) => e.ref)],
      ["schemas", bundle.schemas.map((s) => s.ref)],
      ["taskTypes", bundle.taskTypes.map((t) => t.key)],
    ] as const) {
      for (const dupe of duplicates(values)) {
        ctx.addIssue({
          code: "custom",
          path: [path],
          message: `Duplicate entry '${dupe}'`,
        });
      }
    }
    bundle.schemas.forEach((schema, index) => {
      for (const ref of collectFieldEnumRefs(schema.fields, [])) {
        if (!enumRefs.has(ref)) {
          ctx.addIssue({
            code: "custom",
            path: ["schemas", index, "fields"],
            message: `Unknown enum ref '${ref}'`,
          });
        }
      }
    });
    bundle.taskTypes.forEach((taskType, index) => {
      if (taskType.outcomeEnumRef && !enumRefs.has(taskType.outcomeEnumRef)) {
        ctx.addIssue({
          code: "custom",
          path: ["taskTypes", index, "outcomeEnumRef"],
          message: `Unknown enum ref '${taskType.outcomeEnumRef}'`,
        });
      }
      if (taskType.inputSchemaRef && !schemaRefs.has(taskType.inputSchemaRef)) {
        ctx.addIssue({
          code: "custom",
          path: ["taskTypes", index, "inputSchemaRef"],
          message: `Unknown schema ref '${taskType.inputSchemaRef}'`,
        });
      }
    });
  });

export type TaskTypeBundle = z.infer<typeof TaskTypeBundleSchema>;

/** Validate an uploaded bundle, with readable `path: message` errors. */
export function parseTaskTypeBundle(
  raw: unknown,
): { ok: true; bundle: TaskTypeBundle } | { ok: false; errors: string[] } {
  return parseVersionedBundle(raw, {
    schema: TaskTypeBundleSchema,
    formatVersion: TASK_TYPE_BUNDLE_FORMAT_VERSION,
    label: "task type",
  });
}
export type TaskTypeBundleEnum = z.infer<typeof TaskTypeBundleEnumSchema>;
export type TaskTypeBundleSchemaDefinition = z.infer<
  typeof TaskTypeBundleSchemaDefinitionSchema
>;
export type TaskTypeBundleTaskType = z.infer<
  typeof TaskTypeBundleTaskTypeSchema
>;

// --- Export / import procedures' I/O ---

export const ExportTaskTypeBundleInputSchema = z.object({
  // Omitted = every live task type in the organization. Checklist subtask
  // types are pulled in recursively either way.
  taskTypeIds: z
    .array(z.uuid())
    .min(1)
    .max(MAX_TASK_TYPE_BUNDLE_TASK_TYPES)
    .optional(),
});

export const ExportTaskTypeBundleResponseSchema = z.object({
  bundle: TaskTypeBundleSchema,
  // Settings left out because they point at org-specific records.
  warnings: z.array(z.string()),
});

export const TASK_TYPE_IMPORT_CONFLICT_STRATEGIES = [
  "skip",
  "overwrite",
] as const;

export const ImportTaskTypeBundleInputSchema = z.object({
  bundle: TaskTypeBundleSchema,
  // What to do with a task type whose key already exists here.
  onConflict: z.enum(TASK_TYPE_IMPORT_CONFLICT_STRATEGIES).default("skip"),
  // Plan only: resolve every reuse/create decision and write nothing.
  dryRun: z.boolean().default(false),
});

const ImportDependencyResultSchema = z.object({
  ref: z.string(),
  action: z.enum(["reuse", "create"]),
  // The existing row on reuse; the new row on create (null on a dry run).
  id: z.uuid().nullable(),
  // The name (enum) or slug (schema) used here; may be suffixed on create.
  name: z.string(),
});

export const ImportTaskTypeBundleResponseSchema = z.object({
  dryRun: z.boolean(),
  enums: z.array(ImportDependencyResultSchema),
  schemas: z.array(ImportDependencyResultSchema),
  taskTypes: z.array(
    z.object({
      key: z.string(),
      action: z.enum(["create", "overwrite", "skip"]),
      id: z.uuid().nullable(),
    }),
  ),
  warnings: z.array(z.string()),
});

export type ExportTaskTypeBundleInput = z.infer<
  typeof ExportTaskTypeBundleInputSchema
>;
export type ExportTaskTypeBundleResponse = z.infer<
  typeof ExportTaskTypeBundleResponseSchema
>;
export type ImportTaskTypeBundleInput = z.input<
  typeof ImportTaskTypeBundleInputSchema
>;
export type ImportTaskTypeBundleResponse = z.infer<
  typeof ImportTaskTypeBundleResponseSchema
>;
export type TaskTypeImportConflictStrategy =
  (typeof TASK_TYPE_IMPORT_CONFLICT_STRATEGIES)[number];
